// Phase 9.1.B.2 — Reset password (confirm flow).
//
// Reads ?token=... from the URL (placed there by the email link) and submits
// to POST /portal/auth/password-reset/confirm. Per spec (services/portal-
// auth.ts comment): does NOT auto-login — user is redirected to /login to
// sign in fresh with the new password.
//
// 404 means the token is invalid, expired, or already used. 400 means
// password too short. We surface both as inline form errors.
import { useState, type FormEvent } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { ApiError, api } from "@/lib/api";

//
// Company stores (2026-10-12): /store-invite renders this same page with
// mode="store". The invite / join / store-admin email carries the same kind
// of token (password_reset_tokens, confirmed by the same endpoint), so only
// the words and the landing page differ: "Set a password to start ordering
// from <store>", then sign in → /store.
export function ResetPasswordPage({ mode = "reset" }: { mode?: "reset" | "store" }) {
  const [params] = useSearchParams();
  const token = params.get("token");
  const storeInvite = mode === "store";
  // ?store=<name> — only the welcome line; the token is the credential.
  const storeName = (params.get("store") ?? "").trim().slice(0, 80);
  // ?welcome=1 — the link in the first email after a website order: the
  // account already exists, the customer is choosing their first password.
  const welcome = params.get("welcome") === "1" || storeInvite;
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  if (!token) {
    return <Navigate to="/forgot-password" replace />;
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);

    if (password !== confirmPassword) {
      setError("Passwords don't match.");
      return;
    }
    if (password.length < 12) {
      setError("Password must be at least 12 characters.");
      return;
    }

    setSubmitting(true);
    try {
      await api("/portal/auth/password-reset/confirm", {
        method: "POST",
        body: JSON.stringify({ token, password }),
        skipAuth: true,
      });
      setSuccess(true);
      // Brief pause for the success state, then send to login.
      // A store invite signs in straight into the store.
      setTimeout(
        () => navigate("/login", { replace: true, state: storeInvite ? { from: "/store" } : undefined }),
        1500,
      );
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 404) {
          setError(
            welcome
              ? "That link has expired or was already used. Use Forgot password on the sign-in page to get a fresh one."
              : "That reset link is no longer valid. Request a new one from the sign-in page.",
          );
        } else if (err.status === 400) {
          const body = err.body as { error?: string } | null;
          if (body?.error === "password_too_short") {
            setError("Password must be at least 12 characters.");
          } else {
            setError("Couldn't reset the password. Check the form and retry.");
          }
        } else {
          setError("Something went wrong. Try again.");
        }
      } else {
        setError("Something went wrong. Try again.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  if (success) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-cream-50 px-6 py-16">
        <div className="w-full max-w-md text-center">
          <div className="mb-6 inline-flex h-16 w-16 items-center justify-center rounded-md bg-peach-400">
            <span className="text-2xl font-bold text-brown-500">✓</span>
          </div>
          <h1 className="mb-2 font-display text-3xl font-bold tracking-tight">
            {welcome ? "Password set" : "Password reset"}
          </h1>
          <p className="text-brown-400">Taking you to sign-in…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-cream-50 px-6 py-16">
      <div className="w-full max-w-md">
        <h1 className="mb-2 font-display text-4xl font-bold tracking-tight">
          {welcome ? "Set your password" : "Set a new password"}
        </h1>
        <p className="mb-10 text-brown-400">
          {storeInvite
            ? `Set a password to start ordering from ${storeName || "your company store"}. Use 12 characters or more.`
            : welcome
              ? "Your account is ready — your order and logo are saved to it. Choose a password, 12 characters or more."
              : "Choose something you'll remember — 12 characters or more."}
        </p>

        <form onSubmit={onSubmit} className="space-y-5">
          <label className="block">
            <span className="mb-2 block text-sm font-semibold text-brown-500">
              New password
            </span>
            <input
              type="password"
              required
              minLength={12}
              autoFocus
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full rounded-md border border-cream-400 bg-white px-4 py-3 text-brown-500 placeholder:text-[#8a8a85] shadow-sm focus:border-peach-400 focus:outline-none focus:ring-0"
              autoComplete="new-password"
            />
            <span className="mt-1 block text-xs text-brown-300">
              12+ characters.
            </span>
          </label>

          <label className="block">
            <span className="mb-2 block text-sm font-semibold text-brown-500">
              Confirm new password
            </span>
            <input
              type="password"
              required
              minLength={12}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="w-full rounded-md border border-cream-400 bg-white px-4 py-3 text-brown-500 placeholder:text-[#8a8a85] shadow-sm focus:border-peach-400 focus:outline-none focus:ring-0"
              autoComplete="new-password"
            />
          </label>

          {error && (
            <div className="rounded-md border border-rust-200 bg-rust-50 px-4 py-3 text-sm text-rust-500">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="w-full rounded-md bg-ink px-4 py-3 font-semibold text-white shadow-sm transition-colors hover:bg-ink-700 disabled:opacity-50"
          >
            {submitting ? "Saving…" : welcome ? "Set password" : "Update password"}
          </button>
        </form>

        <p className="mt-8 text-sm text-brown-400">
          <Link
            to="/login"
            className="font-semibold text-ink underline-offset-2 hover:underline"
          >
            Back to sign-in
          </Link>
        </p>
      </div>
    </div>
  );
}
